<script>
    import Vue from "vue";
    import {mapState} from "vuex";
    import {isMobile} from "mobile-device-detect";

    import utils from "@/utils.js";

    export default {
        name: "Login",
        data() {
            return {
                email: "",
                pass: "",
                errorMessage: "",
                unpackAttempts: 0,
                loading: false,
                pin: null,
                usePin: null,
                hasPin: null,
            };
        },
        computed: {
            ...mapState({
                loggedIn: state => state.loggedIn,
                user: state => state.user,
                lastError: state => state.lastError,
            }),
            error: state => state.errorMessage || state.lastError,
        },
        methods: {
            submit() {
                if (!this.loggedIn) {
                    this.login();
                } else {
                    this.unlockBundle();
                }
            },

            login() {
                this.loading = true;
                this.$store
                    .dispatch("login", {email: this.email, pass: this.pass})
                    .then(() => {
                        this.$store.commit("go", "passwords");
                    })
                    .catch(error => {
                        this.loading = false;
                        if (error.response && error.response.data == "unrecognized_email") {
                            this.errorMessage = "E-mail not recognized";
                        } else if (error.response) {
                            // we got an error from server
                            this.errorMessage = "Wrong password";
                        } else {
                            this.errorMessage = error; // just dumping it for now
                        }
                    });
            },

            logOut() {
                this.$store.dispatch("logOut");
            },

            async unlockBundle() {
                this.loading = true;
                if (!this.$store.state.loaded) {
                    // loop till we get storage
                    console.log("waiting for storage");
                    window.setTimeout(this.unlockBundle, 50);
                    return;
                }

                try {
                    let hashedPass = await this.$store.dispatch("hashOnServer", {pass: this.pass});
                    try {
                        this.$store.commit("unlock", hashedPass);
                        this.loading = false;

                        if (isMobile) {
                            // using unpack pass on mobile means pin hasn't been set yet - do it now
                            this.usePin = true;
                            this.focusUnlock();
                        } else {
                            // otherwise demand that we go to passwords listing
                            this.$store.commit("go", "passwords");
                        }
                    } catch (err) {
                        console.log(err);
                        let messages = [
                            "Wrong password",
                            "Maybe check caps and such",
                            "This is not going well",
                            "Maybe it's me, not you.",
                            "Ahh... try again!",
                            "Nah-hah.",
                            "Take your time.",
                            "Still no, but I believe in you!",
                            "Don't give up!",
                            "I'm gonna take a break, my clone process is taking over now.",
                        ];
                        this.errorMessage = messages[this.unpackAttempts % messages.length];
                        this.unpackAttempts += 1;

                        this.loading = false;
                        this.focusUnlock();
                    }
                } catch (error) {
                    // rehash is a straight-forward request, shouldn't fail ever
                    this.loading = false;
                    this.errorMessage = "Bōrk";
                    console.log("Bōrk", error);
                    this.focusUnlock();
                }
            },

            async submitPin() {
                this.loading = true;

                try {
                    await this.$store.dispatch("submitPin", this.pin);
                    this.focusUnlock();
                } catch (error) {
                    let errorMessage = error.response ? error.response.data : error + "";

                    this.loading = false;
                    if (errorMessage == "pin_removed") {
                        this.errorMessage = "Pin removed (too many attempts)";
                        window.localStorage.removeItem("pinData");
                        this.usePin = false;
                    } else if (errorMessage == "profile_failed") {
                        this.errorMessage = "Login expired";
                        window.localStorage.removeItem("pinData");
                        this.usePin = false;
                    } else {
                        this.errorMessage = errorMessage.replace("wrong_pin", "Wrong pin");
                        this.usePin = false;
                    }
                }
            },

            checkPin() {
                this.clearErrors();
                if (this.pin.length == 4) {
                    this.submitPin();
                }
            },

            showSignup() {
                this.$store.commit("go", "signup");
            },
            clearErrors() {
                this.errorMessage = "";
            },

            focusUnlock() {
                Vue.nextTick(() => {
                    let input = this.$refs.email;
                    if (this.loggedIn) {
                        input = this.usePin ? this.$refs.pin : this.$refs.pass;
                    }
                    input.focus();
                    input.select();
                });
            },
        },

        mounted() {
            // on start we try to establish the user
            // if user is not present, we will ask them to log in once they have punched in their packing key
            this.hasPin = (window.localStorage.getItem("pinData") || "").length > 0;
            this.usePin = isMobile && this.hasPin;
            this.focusUnlock();
        },
    };
</script>


<template>
    <div class="login-container">
        <LogoBox :section="loggedIn ? 'Unlock' : 'Log in' " />
        <div class="login-info" v-if="user && loggedIn">
            Logged in as <em>{{ user.email }}</em>
            <div>
                <button @click.prevent="logOut()">
                    Log Out
                </button>
            </div>
        </div>

        <form @submit.prevent="submit" v-if="!usePin">
            <input type="submit" style="display: none" />
            <fieldset :disabled="loading">
                <input
                    name="email"
                    v-if="!loggedIn"
                    v-model="email"
                    type="email"
                    ref="email"
                    placeholder="E-mail"
                    @input="clearErrors"
                />
                <input
                    name="password"
                    v-model="pass"
                    type="password"
                    ref="pass"
                    placeholder="Passphrase"
                    @input="clearErrors"
                />

                <div class="message-box">
                    <div class="loading-message" v-if="loading">{{ loggedIn ? "Loading" : "Logging in" }}&hellip;</div>

                    <div class="unpack-error" v-if="error">
                        {{ error }}
                    </div>

                    <div class="helper" v-if="!loading && !loggedIn && !error">
                        Hit Enter to log in. New to Lockbox?
                        <button @click.prevent="showSignup()">
                            Sign Up
                        </button>
                    </div>

                    <div class="helper" v-if="!loading && user && loggedIn && !error">
                        Hit Enter to unlock
                    </div>
                </div>
            </fieldset>
        </form>

        <form class="unlock-pin" v-if="usePin">
            <input type="submit" style="display: none" />
            <fieldset :disabled="loading">
                <div class="form-title">
                    {{ hasPin ? "Enter Pin" : "Set Up Pin" }}
                </div>

                <input class="pin" v-model="pin" type="password" pattern="[0-9]{4}" ref="pin" @input="checkPin" />

                <div class="message-box">
                    <div class="unpack-error" v-if="error">
                        {{ error }}
                    </div>
                </div>
            </fieldset>
        </form>
    </div>
</template>


<style lang="scss">
    .login-container {
        position: absolute;
        display: flex;
        padding: 2em;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        align-items: center;
        justify-content: center;

        max-width: 1000px;
        margin: 0 auto;

        .logo-box {
            position: absolute;
            top: 0;
            left: 0;
            margin: 40px;
            font-size: 18px;

            gap: 5px 15px;

            .section {
                font-size: 12px;
            }

            svg {
                width: 55px;
            }

        }

        .login-info {
            position: absolute;
            top: 0;
            right: 0;
            text-align: right;
            margin: 40px;
            font-size: 12px;


            em {
                font-style: normal;
                font-weight: 600;
            }
        }

        form {
            display: flex;
            flex-direction: column;

            input {
                font-size: 18px;
                padding: 18px;
                width: 30em;

            }

            fieldset {
                padding: 0;
                border: none;
                display: flex;
                flex-direction: column;
            }
        }

        button {
            text-decoration: underline;
        }

        .message-box {
            height: 2.2em; // don't want the login box shift around when we have no message
            padding: 5px 10px;
            font-size: 0.85em;
            color: #666;
        }

        input {
            margin: 5px 0;
            width: 20em;
            max-width: 85vw;

            &:focus {
                outline: none;
                border: 1px solid #bbb;
            }
        }

        .unpack-error {
            color: $error;
        }

        .helper {
            text-align: right;
            color: #aaa;
            font-size: 12px;
        }

        .pin {
            text-align: center;
            width: 5em;
        }
    }
</style>


